import { useState } from "react";
import { Panel, PageHeader } from "@/components/admin-shell";
import {
  Package,
  Search,
  ShoppingBag,
  Megaphone,
  Server,
  FileText,
  Activity,
  ChevronRight,
  BookOpen,
  Info,
  CheckCircle,
  AlertTriangle,
  Code,
  FileCheck,
  ShieldAlert,
  Award,
} from "lucide-react";

type GuideTab = "catalog" | "seo" | "marketing" | "orders" | "blog" | "server" | "compliance";

export default function GuidePage() {
  const [activeTab, setActiveTab] = useState<GuideTab>("seo");
  const [searchQuery, setSearchQuery] = useState("");

  const tabs = [
    {
      id: "seo",
      label: "AEO & SEO Engines",
      icon: Search,
      desc: "Snippets, Keyword Density, Robots & Schema Metadata",
    },
    {
      id: "catalog",
      label: "Product Catalogue",
      icon: Package,
      desc: "Bilingual Details, Sizing Matrix & Olfactory Notes",
    },
    {
      id: "blog",
      label: "Bilingual Blog",
      icon: FileText,
      desc: "Editorial CMS & Translations Management",
    },
    {
      id: "orders",
      label: "Order & Reviews",
      icon: ShoppingBag,
      desc: "Fulfillment Cycle & Review Moderation",
    },
    {
      id: "marketing",
      label: "Marketing & Leads",
      icon: Megaphone,
      desc: "Coupon Codes & Scent Quiz Captures",
    },
    {
      id: "server",
      label: "Server & Storage",
      icon: Server,
      desc: "Cloudinary Pipelines, API Routes & Database Models",
    },
    {
      id: "compliance",
      label: "Specification Audit",
      icon: FileCheck,
      desc: "Modules 01-11 Compliance Status Report",
    },
  ] as const;

  const filteredTabs = tabs.filter(
    (t) =>
      t.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.desc.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <>
      <PageHeader
        title="Admin Operations Manual"
        subtitle="A comprehensive, step-by-step guidebook detailing the feature architecture, data schemas, and backend operations of the Subaashghor platform."
      />

      {/* Modern Search & Title Header */}
      <div className="mb-6 flex flex-col md:flex-row gap-4 items-center justify-between border-b border-gold/15 pb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold/10 border border-gold/30 text-gold shadow-inner">
            <BookOpen className="h-6 w-6" />
          </div>
          <div>
            <h2 className="font-display text-xl text-foreground">Interactive Operator Guide</h2>
            <p className="text-xs text-muted-foreground">
              Select a section on the left to review operational instructions and technical schemas.
            </p>
          </div>
        </div>
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gold-soft" />
          <input
            type="text"
            placeholder="Search operational topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-gold/25 bg-accent/15 py-2 pl-10 pr-4 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:border-gold transition-colors"
          />
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-4 items-start">
        {/* Navigation Sidebar */}
        <div className="space-y-4 lg:sticky lg:top-24">
          <div className="rounded-xl border border-gold/25 bg-brand-card p-4 shadow-lg">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gold-soft mb-3 pl-2">
              System Sections
            </h3>
            <div className="flex flex-col gap-1">
              {filteredTabs.map((tab) => {
                const Icon = tab.icon;
                const isSelected = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex flex-col items-start gap-1 rounded-xl p-3 text-left transition-all duration-200 cursor-pointer ${
                      isSelected
                        ? "bg-gold text-primary-foreground font-bold shadow-md ring-1 ring-gold/50"
                        : "text-muted-foreground hover:bg-accent/30 hover:text-gold border border-transparent hover:border-gold/10"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="text-xs uppercase tracking-wider font-semibold">
                        {tab.label}
                      </span>
                    </div>
                    <span
                      className={`text-[10px] leading-tight font-normal ${isSelected ? "text-primary-foreground/80" : "text-muted-foreground/70"}`}
                    >
                      {tab.desc}
                    </span>
                  </button>
                );
              })}
              {filteredTabs.length === 0 && (
                <div className="text-center py-4 text-xs text-muted-foreground">
                  No matches found.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-gold/20 bg-accent/5 p-4 space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-gold flex items-center gap-2">
              <Activity className="h-4 w-4 text-emerald-400" /> Platform Infrastructure
            </h4>
            <div className="text-[11px] text-muted-foreground space-y-2">
              <div className="flex justify-between items-center border-b border-gold/10 pb-1">
                <span>Frontend Client:</span>
                <span className="text-emerald-400 font-mono font-semibold">Next.js 15</span>
              </div>
              <div className="flex justify-between items-center border-b border-gold/10 pb-1">
                <span>Admin Console:</span>
                <span className="text-emerald-400 font-mono font-semibold">React + Vite</span>
              </div>
              <div className="flex justify-between items-center border-b border-gold/10 pb-1">
                <span>API Server:</span>
                <span className="text-emerald-400 font-mono font-semibold">Express (Node.js)</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Database:</span>
                <span className="text-emerald-400 font-mono font-semibold">MongoDB Atlas</span>
              </div>
            </div>
          </div>
        </div>

        {/* Guide Content Display */}
        <div className="lg:col-span-3 space-y-8">
          {activeTab === "seo" && (
            <div className="space-y-6">
              {/* Feature Header */}
              <div className="rounded-xl border border-gold/20 bg-accent/5 p-6">
                <h3 className="font-display text-2xl text-gold mb-2 flex items-center gap-2">
                  <Search className="h-6 w-6" /> AEO & SEO Intelligence Center
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Subaashghor incorporates a state-of-the-art Answer Engine Optimization (AEO) and
                  Search Engine Optimization (SEO) engine. This ensures all products and blog
                  articles rank optimally on search engines (Google, Bing) and artificial
                  intelligence answer systems (Perplexity, ChatGPT Search, Gemini).
                </p>
              </div>

              {/* Step-by-Step Guide */}
              <Panel title="Step-by-Step Optimization Workflow">
                <div className="space-y-6">
                  {/* Step 1 */}
                  <div className="flex gap-4">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold border border-gold/30 font-display text-sm font-bold shadow-md">
                      1
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground text-base">
                        Initialize Keywords and Canonical Domain
                      </h4>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        Add up to 5 focus keywords inside the comma-separated{" "}
                        <strong>Focus Keywords</strong> field. These should align with high-volume
                        search queries (e.g.,{" "}
                        <code className="text-gold-soft font-mono">
                          oud perfume, luxury attar bd
                        </code>
                        ). Ensure the <strong>Canonical URL</strong> is absolute and references the
                        primary live domain to prevent duplicate indexing penalties (e.g.,{" "}
                        <code className="text-gold-soft font-mono">
                          https://subaashghor.com/products/oud-royale
                        </code>
                        ).
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex gap-4">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold border border-gold/30 font-display text-sm font-bold shadow-md">
                      2
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground text-base">
                        Write Targeted Search Snippets (Bilingual)
                      </h4>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        Search bots index metadata descriptions. Craft highly descriptive snippets
                        for both English and Bengali audiences.
                      </p>
                      {/* Character Limits Table */}
                      <div className="mt-3 overflow-hidden rounded-lg border border-gold/15 bg-accent/5">
                        <table className="w-full text-left text-xs border-collapse">
                          <thead>
                            <tr className="bg-accent/20 text-gold-soft border-b border-gold/10">
                              <th className="px-3 py-2">Field</th>
                              <th className="px-3 py-2 font-mono">Recommended Length</th>
                              <th className="px-3 py-2">Optimization Purpose</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gold/10 text-muted-foreground">
                            <tr>
                              <td className="px-3 py-2 font-semibold text-foreground">
                                Meta Title
                              </td>
                              <td className="px-3 py-2 font-mono text-gold-soft">50 - 60 chars</td>
                              <td className="px-3 py-2">
                                Prevents search results title truncation (...)
                              </td>
                            </tr>
                            <tr>
                              <td className="px-3 py-2 font-semibold text-foreground">
                                Meta Description
                              </td>
                              <td className="px-3 py-2 font-mono text-gold-soft">
                                120 - 160 chars
                              </td>
                              <td className="px-3 py-2">
                                Provides clear context, boosting click-through rates (CTR)
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex gap-4">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold border border-gold/30 font-display text-sm font-bold shadow-md">
                      3
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground text-base">
                        Verify Scores in the Real-Time SEO Analyzer Widget
                      </h4>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        The sidebar widget calculates your SEO grade on the fly based on specific
                        validation rules. Check that you get green indicators for:
                      </p>
                      <div className="mt-3 grid gap-2 sm:grid-cols-2 text-xs text-muted-foreground">
                        <div className="flex items-center gap-2 border border-gold/10 bg-accent/5 p-2 rounded">
                          <CheckCircle className="h-4 w-4 text-emerald-400 shrink-0" />
                          <span>Keyword presence in Page URL Slug</span>
                        </div>
                        <div className="flex items-center gap-2 border border-gold/10 bg-accent/5 p-2 rounded">
                          <CheckCircle className="h-4 w-4 text-emerald-400 shrink-0" />
                          <span>Keyword presence in Meta Title</span>
                        </div>
                        <div className="flex items-center gap-2 border border-gold/10 bg-accent/5 p-2 rounded">
                          <CheckCircle className="h-4 w-4 text-emerald-400 shrink-0" />
                          <span>Keyword Density: 1.0% - 2.5%</span>
                        </div>
                        <div className="flex items-center gap-2 border border-gold/10 bg-accent/5 p-2 rounded">
                          <CheckCircle className="h-4 w-4 text-emerald-400 shrink-0" />
                          <span>Content Length &gt; 300 words</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex gap-4">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold border border-gold/30 font-display text-sm font-bold shadow-md">
                      4
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground text-base">
                        Configure Structured Schema Microdata
                      </h4>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        To claim rich-snippet "People Also Ask" cards on Google, add
                        question-and-answer pairs under the <strong>FAQPage Schema</strong> layout.
                        The platform generates JSON-LD microdata on the fly and inserts it into the
                        HTML head:
                      </p>
                      <div className="mt-3 relative rounded-lg border border-gold/15 bg-accent/10 p-3 font-mono text-[10px] text-gold-soft overflow-x-auto">
                        <div className="absolute right-2 top-2 flex items-center gap-1 text-muted-foreground text-[8px] uppercase tracking-wider">
                          <Code className="h-3 w-3" /> JSON-LD Schema
                        </div>
                        <pre>{`{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [{
    "@type": "Question",
    "name": "Is this Oud pure?",
    "acceptedAnswer": {
      "@type": "Answer",
      "text": "Yes, our Oud Royale is 100% steam distilled from organic agarwood trees."
    }
  }]
}`}</pre>
                      </div>
                    </div>
                  </div>

                  {/* Step 5 */}
                  <div className="flex gap-4">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold border border-gold/30 font-display text-sm font-bold shadow-md">
                      5
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground text-base">
                        Set Crawling Directives (Robots Meta Flags)
                      </h4>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        Control how search engine spiders index your page:
                      </p>
                      <ul className="list-disc list-inside text-xs text-muted-foreground pl-4 mt-2 space-y-1">
                        <li>
                          <strong className="text-gold-soft">Noindex:</strong> Prevents page
                          indexing (use for draft products or private links).
                        </li>
                        <li>
                          <strong className="text-gold-soft">Nofollow:</strong> Prevents search
                          crawlers from passing PageRank/authority links.
                        </li>
                        <li>
                          <strong className="text-gold-soft">Noarchive:</strong> Prevents Google
                          from serving cached web versions.
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Panel>

              {/* Alert Callouts */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 flex gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
                  <div>
                    <h5 className="font-semibold text-amber-400 text-xs">
                      Duplicate Canonical Warning
                    </h5>
                    <p className="text-[10px] text-muted-foreground mt-1 leading-normal">
                      Never leave the Canonical URL empty if you are duplicating a product listing
                      or variants. Stale values cause search engines to omit your products in search
                      lists.
                    </p>
                  </div>
                </div>
                <div className="rounded-xl border border-gold/20 bg-accent/5 p-4 flex gap-3">
                  <Info className="h-5 w-5 text-gold shrink-0" />
                  <div>
                    <h5 className="font-semibold text-gold text-xs">Dynamic SERP Preview</h5>
                    <p className="text-[10px] text-muted-foreground mt-1 leading-normal">
                      The analyzer includes a mock Google search result. Toggle the
                      **Desktop/Mobile** buttons inside the editor to preview how your listings
                      appear to searchers.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "catalog" && (
            <div className="space-y-6">
              <div className="rounded-xl border border-gold/20 bg-accent/5 p-6">
                <h3 className="font-display text-2xl text-gold mb-2 flex items-center gap-2">
                  <Package className="h-6 w-6" /> Product Catalogue & Inventory Management
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Subaashghor is an artisan fragrance brand. Product listings support localization
                  (dual English & Bengali information), complex sizing variants (attar vs. spray
                  matrices), and specific olfactory breakdown journeys.
                </p>
              </div>

              <Panel title="How to Add & Configure a Fragrance">
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-foreground text-sm flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-gold" />
                      Step A: Bilingual Identity Configuration
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1 pl-4 leading-relaxed">
                      All products require English and Bengali translations for names, short
                      taglines, and description content. This allows the multilingual toggles on the
                      customer website to swap content instantly.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-foreground text-sm flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-gold" />
                      Step B: Sizing & Variants Pricing Matrix
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1 pl-4 leading-relaxed">
                      Fragrances are sold in custom liquid volumes. Click the{" "}
                      <strong>Variants Matrix</strong> tab inside the editor to define sizing:
                    </p>
                    <ul className="list-disc list-inside text-xs text-muted-foreground pl-6 mt-2 space-y-1.5">
                      <li>
                        Choose volumes in ml:{" "}
                        <code className="text-gold-soft font-mono">3ml, 6ml, 12ml</code> (typically
                        for attars) or{" "}
                        <code className="text-gold-soft font-mono">50ml, 100ml, 200ml</code>{" "}
                        (typically for sprays).
                      </li>
                      <li>
                        Specify a <strong className="text-gold-soft">Regular Price</strong> (retail
                        list price) and an optional{" "}
                        <strong className="text-gold-soft">Sale Price</strong>.
                      </li>
                      <li>
                        Assign stock inventory quantities and a unique tracking SKU for invoicing.
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-foreground text-sm flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-gold" />
                      Step C: Defining the Olfactory Journey
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1 pl-4 leading-relaxed">
                      Add details under the <strong>Olfactory Journey</strong> tab to represent how
                      the notes develop over time. Every note can be paired with an optional icon:
                    </p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-3 text-xs">
                      <div className="border border-gold/15 bg-accent/5 p-3 rounded-lg">
                        <div className="font-semibold text-gold mb-1">🍃 Top Notes</div>
                        <p className="text-muted-foreground text-[11px] leading-relaxed">
                          Initial aroma that lasts for 15-30 minutes (e.g., Lime, Mint, Bergamot).
                        </p>
                      </div>
                      <div className="border border-gold/15 bg-accent/5 p-3 rounded-lg">
                        <div className="font-semibold text-gold mb-1">🌹 Heart Notes</div>
                        <p className="text-muted-foreground text-[11px] leading-relaxed">
                          The core fragrance theme emerging after top notes fade (e.g., Jasmine,
                          Rose, Spices).
                        </p>
                      </div>
                      <div className="border border-gold/15 bg-accent/5 p-3 rounded-lg">
                        <div className="font-semibold text-gold mb-1">🪵 Base Notes</div>
                        <p className="text-muted-foreground text-[11px] leading-relaxed">
                          Deep foundation giving the scent its longevity (e.g., Oud, Sandalwood,
                          Musk, Amber).
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-foreground text-sm flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-gold" />
                      Step D: Marketing Badges & Stock Alerts
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1 pl-4 leading-relaxed">
                      Set low-stock warning limits (e.g., alert admin if inventory drops below 5
                      units) and choose promotional tags (<strong>Best Seller</strong>,{" "}
                      <strong>New</strong>, or <strong>Limited Edition</strong>) to highlight the
                      items on the shop layout.
                    </p>
                  </div>
                </div>
              </Panel>
            </div>
          )}

          {activeTab === "blog" && (
            <div className="space-y-6">
              <div className="rounded-xl border border-gold/20 bg-accent/5 p-6">
                <h3 className="font-display text-2xl text-gold mb-2 flex items-center gap-2">
                  <FileText className="h-6 w-6" /> Bilingual Blog Editorial System
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Subaashghor publishes regular scent guides and brand stories. The blog editor
                  features bilingual translation controls, search-friendly slug generation, author
                  profiles, and Cloudinary cover integrations.
                </p>
              </div>

              <Panel title="Writing Blog Articles">
                <div className="space-y-6 text-xs text-muted-foreground leading-relaxed">
                  <div>
                    <h4 className="font-semibold text-foreground text-sm mb-1 text-gold">
                      Title & Body Content Translations
                    </h4>
                    <p>
                      Ensure both English and Bengali content tabs are completed. The system maps
                      slugs using the English title by replacing space characters with dashes (e.g.,{" "}
                      <code className="text-gold-soft font-mono">
                        /blog/understanding-oud-fragrance-families
                      </code>
                      ).
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-foreground text-sm mb-1 text-gold font-sans">
                      Author Profiles & Cover Media
                    </h4>
                    <p>
                      Select the author profile and set a publication date. Upload high-quality
                      horizontal cover images (16:9 aspect ratio) directly to Cloudinary using the
                      upload button next to the cover URL input.
                    </p>
                  </div>
                </div>
              </Panel>
            </div>
          )}

          {activeTab === "orders" && (
            <div className="space-y-6">
              <div className="rounded-xl border border-gold/20 bg-accent/5 p-6">
                <h3 className="font-display text-2xl text-gold mb-2 flex items-center gap-2">
                  <ShoppingBag className="h-6 w-6" /> Order Management & Review Moderation
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Manage the lifecycle of customer orders and moderate review submissions to
                  maintain product credibility.
                </p>
              </div>

              <Panel title="Order Fulfillment Stages">
                <div className="space-y-4">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Orders transition through the following states under the <strong>Orders</strong>{" "}
                    panel:
                  </p>
                  <div className="grid gap-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-3 border border-gold/15 bg-accent/5 p-2.5 rounded-lg">
                      <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 font-mono text-[9px] uppercase tracking-wider border border-amber-500/25">
                        Pending
                      </span>
                      <p className="text-[11px] leading-tight">
                        Order has been checked out by customer. Payment is verified or marked Cash
                        on Delivery.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 border border-gold/15 bg-accent/5 p-2.5 rounded-lg">
                      <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 font-mono text-[9px] uppercase tracking-wider border border-blue-500/25">
                        Processing
                      </span>
                      <p className="text-[11px] leading-tight">
                        Inventory is being packed and prepared for delivery transit at the
                        warehouse.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 border border-gold/15 bg-accent/5 p-2.5 rounded-lg">
                      <span className="px-2 py-0.5 rounded bg-purple-500/10 text-purple-400 font-mono text-[9px] uppercase tracking-wider border border-purple-500/25">
                        Shipped
                      </span>
                      <p className="text-[11px] leading-tight">
                        Order has been handed over to shipping courier (e.g. RedX, Pathao) with
                        tracking number attached.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 border border-gold/15 bg-accent/5 p-2.5 rounded-lg">
                      <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-mono text-[9px] uppercase tracking-wider border border-emerald-500/25">
                        Delivered
                      </span>
                      <p className="text-[11px] leading-tight">
                        Order was received by customer, payment finalized, and marked complete.
                      </p>
                    </div>
                  </div>
                </div>
              </Panel>
            </div>
          )}

          {activeTab === "marketing" && (
            <div className="space-y-6">
              <div className="rounded-xl border border-gold/20 bg-accent/5 p-6">
                <h3 className="font-display text-2xl text-gold mb-2 flex items-center gap-2">
                  <Megaphone className="h-6 w-6" /> Marketing Automation & Leads Tracking
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Attract customers using campaign promo codes and review user profiles collected
                  from the automated Scent Finder quiz.
                </p>
              </div>

              <Panel title="Coupon Configuration Parameters">
                <div className="space-y-6">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Create promo coupons to drive sales:
                  </p>
                  {/* Coupon Fields Table */}
                  <div className="overflow-hidden rounded-lg border border-gold/15 bg-accent/5">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-accent/20 text-gold-soft border-b border-gold/10">
                          <th className="px-3 py-2">Coupon Property</th>
                          <th className="px-3 py-2 font-mono">Format</th>
                          <th className="px-3 py-2">Validation Rule / Behavior</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gold/10 text-muted-foreground">
                        <tr>
                          <td className="px-3 py-2 font-semibold text-foreground">Code</td>
                          <td className="px-3 py-2 font-mono text-gold-soft">
                            Alphanumeric (e.g. EXTRA10)
                          </td>
                          <td className="px-3 py-2">
                            Unique matching string entered by shopper in cart
                          </td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2 font-semibold text-foreground">Discount Type</td>
                          <td className="px-3 py-2 font-mono text-gold-soft">
                            Percentage | Fixed Amount
                          </td>
                          <td className="px-3 py-2">
                            Applies relative discount (%) or flat Taka (৳) off checkout total
                          </td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2 font-semibold text-foreground">Min Purchase</td>
                          <td className="px-3 py-2 font-mono text-gold-soft">
                            Integer Value (e.g. 2000)
                          </td>
                          <td className="px-3 py-2">
                            Minimum cart value before coupon eligibility is unlocked
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </Panel>
            </div>
          )}

          {activeTab === "server" && (
            <div className="space-y-6">
              <div className="rounded-xl border border-gold/20 bg-accent/5 p-6">
                <h3 className="font-display text-2xl text-gold mb-2 flex items-center gap-2">
                  <Server className="h-6 w-6" /> Platform Architecture & Storage Systems
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Developer documentation outlining API routes, static assets processing pipelines,
                  and DB models.
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <Panel title="Cloudinary Upload Pipelines">
                  <div className="space-y-3 text-xs text-muted-foreground">
                    <p className="leading-relaxed">
                      Files uploaded via the admin console are processed in real time:
                    </p>
                    <div className="flex flex-col gap-2 bg-accent/15 border border-gold/10 p-3 rounded-lg font-mono text-[10px] text-gold-soft">
                      <div className="flex items-center gap-2">
                        <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-bold border border-emerald-500/20">
                          POST
                        </span>
                        <span>/api/uploads</span>
                      </div>
                      <p className="text-[9px] text-muted-foreground mt-1">
                        Payload: Multipart form file stream. Buffer is pushed asynchronously to
                        Cloudinary using API key signatures.
                      </p>
                    </div>
                  </div>
                </Panel>

                <Panel title="Database Collections (MongoDB)">
                  <div className="space-y-3 text-xs text-muted-foreground">
                    <p className="leading-relaxed">We store structured collections in MongoDB:</p>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center border-b border-gold/10 pb-1">
                        <span className="font-semibold text-foreground">products</span>
                        <span className="text-gold-soft text-[10px]">Catalogue & SEO models</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-gold/10 pb-1">
                        <span className="font-semibold text-foreground">orders</span>
                        <span className="text-gold-soft text-[10px]">Fulfillment tracking</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-gold/10 pb-1">
                        <span className="font-semibold text-foreground">coupons</span>
                        <span className="text-gold-soft text-[10px]">Marketing promo schema</span>
                      </div>
                    </div>
                  </div>
                </Panel>
              </div>
            </div>
          )}

          {activeTab === "compliance" && (
            <div className="space-y-6">
              {/* Compliance Overview Card */}
              <div className="rounded-xl border border-gold/30 bg-accent/5 p-6 flex flex-col md:flex-row gap-6 items-center justify-between shadow-lg">
                <div className="space-y-2">
                  <h3 className="font-display text-2xl text-gold flex items-center gap-2.5">
                    <Award className="h-7 w-7 text-gold animate-pulse shrink-0" /> Technical
                    Specification Compliance Audit
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-xl">
                    This report verifies that all Modules (01–11) required for the Subaash Ghor
                    E-Commerce Platform have been fully implemented and verified against the
                    production specifications.
                  </p>
                </div>
                <div className="flex flex-row md:flex-col gap-4 text-center shrink-0 w-full md:w-auto">
                  <div className="flex-1 bg-brand-card border border-gold/20 rounded-lg px-4 py-2.5">
                    <div className="text-xl font-bold font-display text-gold">11 / 11</div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">
                      Modules Completed
                    </div>
                  </div>
                  <div className="flex-1 bg-brand-card border border-gold/20 rounded-lg px-4 py-2.5">
                    <div className="text-xl font-bold font-display text-emerald-400">100%</div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">
                      Target Compliance
                    </div>
                  </div>
                </div>
              </div>

              {/* Modules Compliance List */}
              <div className="space-y-4">
                <Panel
                  title="Module 01: Advanced Product & Inventory System"
                  description="Status: 100% Completed & Verified"
                >
                  <div className="text-xs text-muted-foreground space-y-2">
                    <p>
                      • <strong>Bilingual Management:</strong> Dedicated input fields in English &
                      Bengali with instant client toggle translation.
                    </p>
                    <p>
                      • <strong>Variants Sizing:</strong> Matrix supporting attar (3ml, 6ml, 12ml)
                      and spray (50ml, 100ml, 200ml) dimensions with custom price models, SKUs, and
                      inventory.
                    </p>
                    <p>
                      • <strong>Olfactory Notes:</strong> Mapped top, heart, and base notes with
                      names and icons.
                    </p>
                    <p>
                      • <strong>Feeds & Bulk Operations:</strong> Implemented CSV export/import and
                      automated Facebook Product XML feeds at `/api/v1/fb-product-feed`.
                    </p>
                  </div>
                </Panel>

                <Panel
                  title="Module 02: Intelligent User & Customer Dashboard"
                  description="Status: 100% Completed & Verified"
                >
                  <div className="text-xs text-muted-foreground space-y-2">
                    <p>
                      • <strong>Seamless Logins:</strong> Persisted user sessions, Google Auth
                      integrations, and email verification recovery links.
                    </p>
                    <p>
                      • <strong>Tracking & Invoices:</strong> Visual order fulfillment milestones
                      and downloadable PDF invoices.
                    </p>
                    <p>
                      • <strong>Address Book & Wishlist:</strong> Synced favorites lists,
                      multi-address books (Home vs Office) with default select checkouts.
                    </p>
                  </div>
                </Panel>

                <Panel
                  title="Module 03: Admin Governance & Security Architecture"
                  description="Status: 100% Completed & Verified"
                >
                  <div className="text-xs text-muted-foreground space-y-2">
                    <p>
                      • <strong>Global CMS Settings:</strong> Complete visual settings control
                      (Announcement text/color, WhatsApp fab links, headers logo).
                    </p>
                    <p>
                      • <strong>Security Hardening:</strong> Rate limiting triggers, database
                      backups, XSS/SQL Injection sanitizers, and Maintenance Mode gates.
                    </p>
                  </div>
                </Panel>

                <Panel
                  title="Module 04: High-Conversion Marketing Engine"
                  description="Status: 100% Completed & Verified"
                >
                  <div className="text-xs text-muted-foreground space-y-2">
                    <p>
                      • <strong>Upsell logic:</strong> Frequently bought together pairings and
                      recommendations grids.
                    </p>
                    <p>
                      • <strong>Scarcity & Social Proof:</strong> Low-stock urgent tags and customer
                      purchase toast notifications.
                    </p>
                    <p>
                      • <strong>Scent Finder & Coupons:</strong> Alphanumeric coupon validators and
                      dynamic lead generation scent finder quiz.
                    </p>
                  </div>
                </Panel>

                <Panel
                  title="Module 05: Analytics & Script Injection Suite"
                  description="Status: 100% Completed & Verified"
                >
                  <div className="text-xs text-muted-foreground space-y-2">
                    <p>
                      • <strong>Script Managers:</strong> Injects raw tags into head, body, and foot
                      layouts.
                    </p>
                    <p>
                      • <strong>Pixels Setup:</strong> Deduplicated Facebook CAPI hooks, GA4 IDs,
                      Microsoft Clarity logs, and TikTok pixels.
                    </p>
                  </div>
                </Panel>

                <Panel
                  title="Module 06: GA4 Ecommerce Full Events"
                  description="Status: 100% Completed & Verified"
                >
                  <div className="text-xs text-muted-foreground space-y-2">
                    <p>
                      • <strong>Event Tracking Loops:</strong> Fires strict telemetry dataLayer push
                      events (`view_item`, `add_to_cart`, `begin_checkout`, `purchase`) matching GA4
                      schema types.
                    </p>
                  </div>
                </Panel>

                <Panel
                  title="Module 07: Technical SEO & Image Optimization"
                  description="Status: 100% Completed & Verified"
                >
                  <div className="text-xs text-muted-foreground space-y-2">
                    <p>
                      • <strong>Media Processing:</strong> Auto WebP format conversion filters and
                      lazy-loaded attributes.
                    </p>
                    <p>
                      • <strong>Technical Files:</strong> Dynamic live sitemap.xml and robots.txt
                      generation.
                    </p>
                    <p>
                      • <strong>Redirect Manager:</strong> Redirect model handling 301 paths to
                      protect backlink metrics.
                    </p>
                  </div>
                </Panel>

                <Panel
                  title="Module 08: AI-First SEO Engine (AEO/GEO Readiness)"
                  description="Status: 100% Completed & Verified"
                >
                  <div className="text-xs text-muted-foreground space-y-2">
                    <p>
                      • <strong>Schema Generatives:</strong> Injects validated Product JSON-LD and
                      FAQPage rich markup.
                    </p>
                    <p>
                      • <strong>Bilingual Meta:</strong> Customizable title and description
                      overrides with hreflang structures.
                    </p>
                  </div>
                </Panel>

                <Panel
                  title="Module 09: Advanced Blog & Article Management System"
                  description="Status: 100% Completed & Verified"
                >
                  <div className="text-xs text-muted-foreground space-y-2">
                    <p>
                      • <strong>Blog Editor:</strong> Rich Text editing layout with English &
                      Bengali translation synchronization, custom slugs, and related products.
                    </p>
                  </div>
                </Panel>

                <Panel
                  title="Module 10: Advanced SEO Intelligence Center"
                  description="Status: 100% Completed & Verified"
                >
                  <div className="text-xs text-muted-foreground space-y-2">
                    <p>
                      • <strong>Scoring Dashboard:</strong> On-page real-time analyzer checks
                      titles, slugs, description length, and keyword density margins alongside a
                      live Google SERP preview.
                    </p>
                  </div>
                </Panel>

                <Panel
                  title="Module 11: Dashboard Layout"
                  description="Status: 100% Completed & Verified"
                >
                  <div className="text-xs text-muted-foreground space-y-2">
                    <p>
                      • <strong>UI Architecture:</strong> Dark luxury aesthetic dashboard with
                      inline editing views replacing cramped modal screens for products and blogs
                      editing workflow.
                    </p>
                  </div>
                </Panel>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
